// KanbanItem.tsx
import { WarningOutlined } from "@ant-design/icons";
import { Pagination, Spin, Tooltip } from "antd";
import moment from "moment";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from 'react-router-dom';
import { SearchTaskByKanbanResponse } from 'services/Main/purchaseOrder_services';
import { services } from 'stores/root_store';
import { ROUTES } from 'base/routes/routes';

import warning from 'assets/images/warning.png'
import packageBox from 'assets/images/image96.png'
import threeBox from 'assets/images/image14.png'
import cartList from 'assets/images/image20.png'
import { FaRecycle } from 'react-icons/fa';

import balance from 'assets/images/balance.png'
import image15 from 'assets/images/image15.png'
import image6 from 'assets/images/image6.png'

// ===================== TYPES =====================
export type KanbanAttachment = {
  id: string;
  referenceId: string;
  fileName: string;
  fileUrl: string;
};

export type KanbanCardItem = {
  id: string;
  title: string;
  subTitle?: string;
  metaLeft?: string;
  metaRight?: string;
  workflowName?: string;
  vendorName?: string;
  licensePlate?: string;
  deliveryTimeText?: string;
  driverName?: string;
  productText?: string;
  checkInText?: string;
  checkOutText?: string;
  isShowRejectReason?: boolean;
  rejectReason?: string;
  statusText?: string;
  amountText?: string;
  attachments?: KanbanAttachment[];
  raw?: any;
  color?: string;
  version?: string;
};

export type KanbanApiItem = SearchTaskByKanbanResponse;

const formatDateTime = (value?: string | null) => {
  if (!value) return "";
  const date = moment(value);
  return date.isValid() ? date.format("HH:mm DD/MM/YYYY") : "";
};

// Xác định icon và tooltip dựa trên workflowCode và kanbanIcon
const getKanbanIcon = (item: any) => {
  const isWasteMaterial = item?.raw?.workflowCode === 'WasteMaterial';
  const isMaterial = item?.raw?.workflowCode === 'AVN_WF';
  const kanbanIcon = item?.raw?.kanbanIcon;

  if (isWasteMaterial) {
    // Chất thải: dùng kanbanIcon để phân biệt nguy hại (WARNING) và thông thường (RECYCLE)
    if (kanbanIcon === 'WARNING') {
      return (
        <Tooltip title="Chất thải nguy hại">
          <img src={warning} className='w-5' alt="warning" />
        </Tooltip>
      );
    } else if (kanbanIcon === 'RECYCLE') {
      return (
        <Tooltip title="Chất thải thông thường & rác sinh hoạt">
          <FaRecycle className="text-green-500" style={{ fontSize: '16px' }} />
        </Tooltip>
      );
    } else if (kanbanIcon === 'BOX') {
      // Nguyên vật liệu: dùng icon box màu cam
      return (
        <Tooltip title="Nguyên vật liệu">
          {/* <FaBox className="text-orange-500" style={{ fontSize: '16px' }} /> */}
          <img src={packageBox} className='w-5' alt="package-box" />
        </Tooltip>
      );
    }
    else {
      // Mặc định cho chất thải
      return (
        <Tooltip title="Chất thải">
          <WarningOutlined className="text-yellow-500" style={{ fontSize: '16px' }} />
        </Tooltip>
      );
    }
  } else if (isMaterial) {
    // Nguyên vật liệu: dùng icon box màu cam
    return (
      <Tooltip title="Nguyên vật liệu">
        {/* <FaBox className="text-orange-500" style={{ fontSize: '16px' }} /> */}
        <img src={packageBox} className='w-5' alt="package-box" />
      </Tooltip>
    );
  }
  return null;
};

const getColumnIcon = (image: any) => {
  switch (image) {
    case 'DELIVERY_TRUCK':
      return <img src={image6} className='w-10' alt="delivery-truck" />;
    case 'SCALE':
      // return <img src={image14} className='w-8' />;
      return <img src={balance} className='w-8' alt="scale" />;
    case 'BOX':
      return <img src={packageBox} className='w-8' alt="box" />;
    case 'OUT_TRUCK':
      return <img src={image15} className='w-10' alt="out-truck" />;
    case 'THREEBOX':
      return <img src={threeBox} className='w-10' alt="three-box" />;
    case 'CARTLIST':
      return <img src={cartList} className='w-10' alt="cart-list" />;
    default:
      return <img src={image6} className='w-10' alt="default-truck" />;
  }
};

const isWasteMaterialWorkflow = (item: KanbanCardItem) => item?.raw?.workflowCode === 'WasteMaterial';

const isMaterialWorkflow = (item: KanbanCardItem) => item?.raw?.workflowCode === 'AVN_WF';

const getWorkflowLabel = (item: KanbanCardItem) => {
  if (isWasteMaterialWorkflow(item)) return 'Chất thải';
  if (isMaterialWorkflow(item)) return 'NVL';
  return item.workflowName || '';
};

const getTimeLabel = (item: KanbanCardItem) => {
  if (isWasteMaterialWorkflow(item)) return 'TG dự kiến';
  if (isMaterialWorkflow(item)) return 'TG dự kiến';
  return 'TG dự kiến';
};

// ===================== MAP FUNCTIONS =====================
export function mapKanbanApiItemToCard(x: KanbanApiItem): KanbanCardItem {
  const productText =
    [x.productCode, x.productName].filter(Boolean).join(" | ") ||
    [x.orderTypeCode, x.orderTypeName].filter(Boolean).join(" | ");

  const deliveryTime = x.deliveryTime || x.documentDate || null;
  const vendorName = x.vendorName || x.customerName || "";
  const amountText =
    typeof x.totalAmount === "number"
      ? `${(x.totalAmount || 0).toLocaleString("vi-VN")} ${x.currency || "đ"}`
      : undefined;

  return {
    id: x.taskId || x.salesOrderId || x.title || String(x.stt),
    title: x.title || x.summary || "",
    subTitle: x.customerName,
    metaLeft: [x.orderTypeCode, x.orderTypeName].filter(Boolean).join(" - "),
    metaRight: x.distributionChannel,
    workflowName: x.workflowName,
    vendorName,
    licensePlate: x.licensePlate,
    deliveryTimeText: formatDateTime(deliveryTime),
    driverName: x.driverName,
    productText,
    checkInText: formatDateTime(x.checkInDate),
    checkOutText: formatDateTime(x.checkOutDate),
    isShowRejectReason: Boolean(x.isShowRejectReason),
    rejectReason: x.rejectReason || "",
    statusText: x.taskStatusName,
    amountText,
    color: x.color || "",
    attachments: [],
    raw: x,
    version: x.versionNo,
  };
}

// ===================== UI COMPONENTS =====================
const KanbanHeader = ({ title, count, image }: { title: string; count?: number; image?: React.ReactNode }) => {
  return (
    <div className="!min-h-16 flex justify-center items-center px-1 py-2 border-b bg-white sticky top-0 z-10 text-center font-bold">
      <div className='mr-2'>
        {image}
      </div>
      <div className="text-[13px]">{title}</div>
      {/* comment tạm, đừng bỏ */}
      {/* <div className="bg-primary ml-2 text-white rounded-full w-6 h-6 inline-flex items-center justify-center text-xs">
        {Number.isInteger(count) ? ((count || 0) > 99 ? "99+" : count) : ""}
      </div> */}
    </div>
  );
};

const KanbanCard = ({
  item,
  onClick,
  href
}: {
  item: KanbanCardItem;
  onClick?: (it: KanbanCardItem) => void;
  href?: string;
}) => {
  const attachCount = item.attachments?.length || 0;
  const isWasteMaterial = isWasteMaterialWorkflow(item);
  const workflowLabel = getWorkflowLabel(item);
  const showCheckInOut = Boolean(item.checkInText || item.checkOutText);
  const isShowRejectReason = Boolean(item.isShowRejectReason);
  const rejectReason = item.rejectReason;
  const isCreateBySystem = Boolean(item?.raw?.isCreateBySystem);


  return (
    //Theo yêu cầu thì chị Thảo 
    //Muốn chuột phải open new link và chuột trái thì vẫn giữ nguyên hành vi onClick
    //Nên tạm thời thẻ a style đỡ style in line ở đây để làm nhanh, nếu có thời gian thì refactor
    <Link
      style={{
        backgroundColor: item?.color,
        textDecoration: 'none',
        color: 'inherit',
        display: 'block'
      }}
      className="bg-white rounded-xl shadow-sm border px-1 py-2 cursor-pointer hover:shadow-md transition"
      // onClick={() => onClick?.(item)}
      to={href || ''}
      target="_blank"
      rel="noreferrer"
    >
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <div className="flex items-start gap-2 mb-1">
            <div className="w-5 h-5 rounded-lg flex items-center justify-center text-green-500">
              {/* <InboxOutlined className="text-base" /> */}
              {getKanbanIcon(item)}
            </div>
            <div className={`uppercase font-semibold text-xs break-words ${isCreateBySystem ? "text-[#dc2626]" : ""}`} title={item.title}>
              {item.title}
            </div>
          </div>
        </div>

        {/* không cần hiện những cái NVL nữa */}
        {workflowLabel && isWasteMaterial ? (
          <div
            className="text-white text-[10px] font-semibold rounded-lg px-1 py-0.5 whitespace-nowrap"
            style={{ backgroundColor: isWasteMaterial ? '#dc2626' : '#069e15' }}
          >
            {workflowLabel}
          </div>
        ) : null}
      </div>

      <div className="text-xs break-words">
        <div>
          <span className="font-semibold">{item.vendorName || ""}</span>
        </div>
        <div>
          Số xe: <span className="font-semibold">{item.licensePlate || ""}</span>
        </div>
        <div>
          {getTimeLabel(item)}: <span className="font-semibold">{item.deliveryTimeText || ""}</span>
        </div>
        <div>
          Tài xế: <span className="font-semibold">{item.driverName || ""}</span>
        </div>
        {isWasteMaterial && item.productText ? (
          <div>
            CT/PL: <span className="font-semibold">{item.productText}</span>
          </div>
        ) : null}
        {showCheckInOut ? (
          <>
            <div>
              Vào cổng: <span className="font-semibold">{item.checkInText || ""}</span>
            </div>
            <div>
              Ra cổng: <span className="font-semibold">{item.checkOutText || ""}</span>
            </div>
          </>
        ) : null}
        {isShowRejectReason ? (
          <div>
            Lý do từ chối: <span className="font-semibold">{rejectReason}</span>
          </div>
        ) : null}
      </div>

      {attachCount > 0 ? (
        <Tooltip title={undefined}>
          <span
            className="text-xs text-gray-500 inline-flex items-center gap-1 mt-1"
            onClick={(e) => e.stopPropagation()}
          >
            📎 {attachCount}
          </span>
        </Tooltip>
      ) : null}
    </Link>
  );
};

// ===================== COLUMN FOOTER (pageSize + Pagination) =====================
const ColumnFooter = ({
  pageIndex,
  pageSize,
  total,
  onChangePageSize,
  onChangePage,
  loading,
}: {
  pageIndex: number;
  pageSize: number;
  total: number;
  onChangePageSize: (ps: number) => void;
  onChangePage: (pageIndex: number) => void;
  loading: boolean;
}) => {
  return (
    <div className="border-t bg-white px-2 py-2">
      <div className="flex items-center justify-between gap-2">
        <span className="flex items-center gap-2">
          {/* <Translation>{(t) => <>{t("View")}</>}</Translation>
          <CustomSelect
            size="small"
            className="min-w-[72px] !rounded-none"
            value={String(pageSize)}
            onChange={(value: any) => onChangePageSize(Number(value))}
          >
            {PAGE_SIZE_OPTION.map((ps) => (
              <Select.Option key={ps} value={ps}>
                {ps}
              </Select.Option>
            ))}
          </CustomSelect>
          <Translation>{(t) => <>{t("Item")}</>}</Translation> */}
        </span>

        <div className="flex items-center">
          <Pagination
            // size="small"
            current={pageIndex}
            pageSize={pageSize}
            total={total}
            showSizeChanger={false}
            disabled={loading}
            onChange={(p) => onChangePage(p)}
          />
        </div>
      </div>
    </div>
  );
};

// ===================== PER-COLUMN STATE =====================
type ColumnMetaFromApi = {
  value?: string;
  label?: string;
  key?: string;
  code?: string;
  totalCount?: number;
  image?: string;
  color?: string;
};

type ColumnPaging = { pageIndex: number; pageSize: number };

type ColumnState = {
  key: string;
  title: string;
  totalCount: number;
  loading: boolean;
  paging: ColumnPaging;
  items: KanbanCardItem[];
  image?: string;
  color?: string;
};

type ColumnStateMap = Record<string, ColumnState>;

// ===================== COLUMN VIEW =====================
const KanbanColumnView = ({
  column,
  onCardClick,
  disabled = false,
  onChangePageSize,
  onChangePageIndex,
}: {
  column: ColumnState;
  onCardClick?: (it: KanbanCardItem) => void;
  disabled?: boolean;
  onChangePageSize: (ps: number) => void;
  onChangePageIndex: (pageIndex: number) => void;
}) => {
  return (
    <div
      className="max-h-[600px] flex flex-col h-full rounded-lg overflow-hidden">
      <KanbanHeader title={column.title} count={column.totalCount} image={getColumnIcon(column?.image)} />

      <div className="p-1 flex-1 overflow-auto relative !bg-[#f3f4f6] [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]">
        {column.loading && (column.items?.length || 0) === 0 ? (
          <div className="w-full flex items-center justify-center py-10">
            <Spin />
          </div>
        ) : (
          <div className="flex flex-col gap-2">
            {column.items.map((it) => {
              const url = getRoute(it); // Tính toán URL ở đây
              return (
                <div key={it.id}>
                  {/* Bỏ onClick ở div bao ngoài này nếu KanbanCard đã dùng thẻ <a>, 
         để tránh bị nhảy link 2 lần hoặc xung đột.
       */}
                  <KanbanCard item={it} href={url} />
                </div>
              );
            })}
          </div>
        )}
      </div>

      <ColumnFooter
        pageIndex={column.paging.pageIndex}
        pageSize={column.paging.pageSize}
        total={column.totalCount}
        loading={column.loading}
        onChangePageSize={onChangePageSize}
        onChangePage={onChangePageIndex}
      />
    </div>
  );
};

// ===================== KANBAN ITEMS =====================
export type KanbanItemsProps = {
  dataSearch: object;
  disabled?: boolean;
  onCardClick?: (card: KanbanCardItem) => void;
  className?: string;
  pageSizeMain?: number;
};

export const KanbanItems = ({
  disabled = false,
  onCardClick,
  className,
  dataSearch,
  pageSizeMain
}: KanbanItemsProps) => {
  const navigate = useNavigate();
  const [columns, setColumns] = useState<ColumnStateMap>({});
  const [columnsOrder, setColumnsOrder] = useState<string[]>([]);
  const [boardLoading, setBoardLoading] = useState(false);

  // giữ ref latest columns để fetchColumn không bị stale closure
  const columnsRef = useRef<ColumnStateMap>({});
  useEffect(() => {
    columnsRef.current = columns;
  }, [columns]);

  // request id để bỏ qua response cũ khi search/filter đổi
  const boardReqIdRef = useRef(0);

  // per-column request id (tránh response cũ đè response mới của chính cột đó)
  const colReqIdRef = useRef<Record<string, number>>({});

  const columnEntries = useMemo(() => {
    return columnsOrder
      .map((key) => (columns[key] ? { key, value: columns[key] } : null))
      .filter(Boolean) as Array<{ key: string; value: ColumnState }>;
  }, [columns, columnsOrder]);

  const gridTemplateColumns = useMemo(() => {
    const columnCount = Math.max(columnEntries.length, 1);
    return `repeat(${columnCount}, minmax(0, 1fr))`;
  }, [columnEntries.length]);

  const dataSearchKey = useMemo(() => JSON.stringify(dataSearch || {}), [dataSearch]);

  // init columns: giữ paging/items cũ nếu cùng code (đỡ chớp rỗng)
  const initColumnsFromApi = useCallback((cols: ColumnMetaFromApi[]) => {
    setColumns((prev) => {
      const nextMap: ColumnStateMap = {};
      const nextOrder: string[] = [];

      for (const c of cols || []) {
        const code = (c?.code || "").toString();
        if (!code) continue;

        nextOrder.push(code);

        const prevCol = prev?.[code];

        nextMap[code] = {
          key: code,
          title: (c?.label || c?.value || code).toString(),
          totalCount: Number(c?.totalCount || 0),
          loading: false,
          image: c?.image || "",
          color: c?.color || "",
          paging: prevCol?.paging || { pageIndex: 1, pageSize: pageSizeMain || 10 },
          // giữ items cũ nếu còn (đỡ “Không có dữ liệu” nháy)
          items: prevCol?.items || [],
        };
      }

      // set order ở đây luôn
      setColumnsOrder(nextOrder);
      return nextMap;
    });
  }, [pageSizeMain]);

  const getColumnKanban = useCallback(async () => {
    const reqId = ++boardReqIdRef.current;
    setBoardLoading(true);

    try {
      const rs = await services.purchaseOrderServices.getColumnKanban(dataSearch);

      // nếu có request mới hơn -> bỏ qua
      if (reqId !== boardReqIdRef.current) return;

      if (rs?.isSuccess) {
        initColumnsFromApi(rs?.data || []);
      } else {
        setColumns({});
        setColumnsOrder([]);
      }
    } finally {
      // vẫn check reqId để tránh “tắt loading” sai lúc
      if (reqId === boardReqIdRef.current) {
        setBoardLoading(false);
      }
    }
  }, [dataSearch, initColumnsFromApi]);

  // fetch list per column, có token chống stale
  const fetchColumn = useCallback(
    async (columnCode: string, pagingOverride?: ColumnPaging) => {
      const boardReqId = boardReqIdRef.current;
      const col = columnsRef.current?.[columnCode];
      if (!col) return;

      const paging = pagingOverride ?? col.paging;

      const colReqId = (colReqIdRef.current[columnCode] || 0) + 1;
      colReqIdRef.current[columnCode] = colReqId;

      // đừng set items = [] ở đây (giữ list cũ + loading)
      setColumns((prev) => ({
        ...prev,
        [columnCode]: { ...prev[columnCode], loading: true, paging },
      }));
      const rs = await services.purchaseOrderServices.searchTaskByKanban({
        ...dataSearch,
        kanbanColumn: columnCode,
        kanbanCode: columnCode,
        paging,
      })


      // bỏ qua nếu board đã đổi filter
      if (boardReqId !== boardReqIdRef.current) return;

      // bỏ qua nếu cột có request mới hơn
      if (colReqId !== colReqIdRef.current[columnCode]) return;

      if (rs?.isSuccess) {
        const res = rs.data as any;
        const apiItems: KanbanApiItem[] = (res || []) as KanbanApiItem[];
        const mapped = (apiItems || []).map(mapKanbanApiItemToCard);

        setColumns((prev) => ({
          ...prev,
          [columnCode]: {
            ...prev[columnCode],
            loading: false,
            items: mapped,
            paging,
            // totalCount: nếu BE không trả theo search -> giữ theo cột
            totalCount: prev[columnCode]?.totalCount ?? 0,
          },
        }));
      } else {
        setColumns((prev) => ({
          ...prev,
          [columnCode]: { ...prev[columnCode], loading: false },
        }));
      }
    },
    [dataSearch]
  );

  // ===================== EFFECTS =====================
  useEffect(() => {
    // mỗi lần search/filter đổi => tăng reqId, reset per-column req
    boardReqIdRef.current += 1;
    colReqIdRef.current = {};

    // load columns
    getColumnKanban();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataSearchKey]);

  // khi có columnsOrder -> load từng cột (song song cũng được vì có token, nhưng tuần tự OK)
  useEffect(() => {
    if (!columnsOrder.length) return;

    const localBoardReqId = boardReqIdRef.current;

    (async () => {
      for (const code of columnsOrder) {
        if (localBoardReqId !== boardReqIdRef.current) return;

        const pageSize = columnsRef.current?.[code]?.paging?.pageSize || 10;
        await fetchColumn(code, { pageIndex: 1, pageSize });
      }
    })();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [columnsOrder.join("|"), dataSearchKey, fetchColumn]);


  // khi pageSizeMain đổi -> load lại từng cột với pageSize mới
  useEffect(() => {
    if (!columnsOrder.length) return;

    const localBoardReqId = boardReqIdRef.current;

    (async () => {
      for (const code of columnsOrder) {
        if (localBoardReqId !== boardReqIdRef.current) return;

        const pageSize =
          pageSizeMain ?? columnsRef.current?.[code]?.paging?.pageSize ?? 10;

        await fetchColumn(code, { pageIndex: 1, pageSize });
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [columnsOrder.join("|"), dataSearchKey, fetchColumn, pageSizeMain]);


  // ===================== HANDLERS =====================
  // Xác định route dựa trên workflowCode
  const getRoute = (data: KanbanCardItem) => {
    console.log('data👉👉👉👉👉👉👉👉👉👉👉', data);

    const workflowCode = data?.raw?.workflowCode;
    if (workflowCode === 'WasteMaterial') {
      return ROUTES.WASTE_HANDOVER_REGISTRATION.EDIT_REGISTRATION.LINK + '/' + data.id;
    } else {
      return ROUTES.PURCHASE_ORDER.LIST_PO.EDIT_VOTE.LINK + '/' + data.id;
    }
  };

  const handleCardClick = (item: KanbanCardItem) => {
    const route = getRoute(item);
    window.open(route, '_blank');
  };

  const handleChangePageSize = (code: string, pageSize: number) => {
    const nextPaging = { pageIndex: 1, pageSize };

    // không clear items ngay (giữ list cũ + loading)
    setColumns((prev) => ({
      ...prev,
      [code]: {
        ...prev[code],
        paging: nextPaging,
      },
    }));

    fetchColumn(code, nextPaging);
  };

  const handleChangePageIndex = (code: string, pageIndex: number) => {
    const col = columnsRef.current?.[code];
    if (!col) return;

    const nextPaging = { ...col.paging, pageIndex };

    setColumns((prev) => ({
      ...prev,
      [code]: {
        ...prev[code],
        paging: nextPaging,
      },
    }));

    fetchColumn(code, nextPaging);
  };

  return (
    <div className={`${className || ""} overflow-x-hidden`}>
      {boardLoading && columnEntries.length === 0 ? (
        <div style={{ minHeight: 200, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Spin tip="Đang tải dữ liệu..." />
        </div>
      ) : (
        <Spin spinning={boardLoading} tip="Đang tải dữ liệu...">
          <div className="w-full overflow-x-hidden" style={{ minHeight: 200 }}>
            <div
              className="grid gap-1"
              style={{
                gridTemplateColumns,
              }}
            >
              <style>
                {`
            .custom-scrollbar::-webkit-scrollbar {
              width: 4px;
            }
            .custom-scrollbar::-webkit-scrollbar-track {
              background: #f1f1f1;
            }
            .custom-scrollbar::-webkit-scrollbar-thumb {
              background: #888;
              border-radius: 4px;
            }
            .custom-scrollbar::-webkit-scrollbar-thumb:hover {
              background: #555;
            }
          `}
              </style>
              {columnEntries.map(({ key, value }) => (
                <KanbanColumnView
                  key={key}
                  column={value}
                  onCardClick={handleCardClick}
                  disabled={disabled}
                  onChangePageSize={(ps) => handleChangePageSize(key, ps)}
                  onChangePageIndex={(p) => handleChangePageIndex(key, p)}
                />
              ))}
            </div>
          </div>
        </Spin>
      )}
    </div>
  );
};

const getRoute = (data: KanbanCardItem) => {
  const workflowCode = data?.raw?.workflowCode;
  if (workflowCode === 'WasteMaterial') {
    return ROUTES.WASTE_HANDOVER_REGISTRATION.EDIT_REGISTRATION.LINK + '/' + data.id;
  }
  return ROUTES.PURCHASE_ORDER.LIST_PO.EDIT_VOTE.LINK + '/' + data.id;
};